import { PlatformApiError } from "./api";
import type { SupportSession } from "./types";

type Fetcher = typeof fetch;
interface Envelope<T> { data?: T; error?: { code?: string; message?: string }; requestId?: string }

export class SupportApiClient {
  private csrfToken: string | null = null;
  private readonly fetcher: Fetcher;

  constructor(fetcher: Fetcher = fetch) {
    this.fetcher = fetcher.bind(globalThis);
  }

  private async csrf(): Promise<string> {
    const response = await this.fetcher("/api/v1/platform/auth/csrf", { credentials: "include" });
    const body = await response.json() as Envelope<{ authenticated: boolean; csrfToken: string }>;
    if (!response.ok || !body.data?.csrfToken) {
      throw new PlatformApiError(response.status, body.error?.code ?? "csrf_failed", body.error?.message ?? "No se pudo preparar el acceso de soporte.", body.requestId);
    }
    this.csrfToken = body.data.csrfToken;
    return this.csrfToken;
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const method = (init.method ?? "GET").toUpperCase();
    const headers = new Headers(init.headers);
    if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
      headers.set("content-type", "application/json");
      headers.set("x-csrf-token", this.csrfToken ?? await this.csrf());
    }
    const response = await this.fetcher(`/api/v1/platform/support${path}`, { ...init, method, credentials: "include", headers });
    const body = await response.json().catch(() => ({})) as Envelope<T>;
    if (!response.ok) {
      if (response.status === 401) this.csrfToken = null;
      throw new PlatformApiError(response.status, body.error?.code ?? `support_${response.status}`, body.error?.message ?? "No se pudo completar el acceso de soporte.", body.requestId);
    }
    return body.data as T;
  }

  current(): Promise<{ supportSession: SupportSession | null }> {
    return this.request("/session");
  }

  start(input: { organizationId: string; reason: string; returnTo: string }): Promise<{ supportSession: SupportSession }> {
    return this.request("/sessions", { method: "POST", body: JSON.stringify(input) });
  }

  end(): Promise<{ supportSession: SupportSession | null }> {
    return this.request("/session", { method: "DELETE" });
  }
}
